"use client";

import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment-timezone";
import {
  Calendar as CalendarIcon,
  Clock,
  ChevronDown,
  ChevronUp,
  MapPin,
} from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import { Button } from "@baluu/ui/components/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@baluu/ui/components/card";
import { Calendar } from "@baluu/ui/components/calendar";
import { CheckoutSteps } from "@baluu/ui/components/checkout/checkout-steps";

type PricingOption = {
  id: string;
  name: string;
  description?: string;
  price: number;
  duration: number;
  ticketsIds?: string[];
};

type AvailableSlotRaw = {
  startMinutes: number;
  endMinutes: number;
  startUTC?: string;
  endUTC?: string;
  scheduleSlotId: string;
  ticketIndex: number;
};

type AvailableDate = {
  dateISO: string;
  isDisabled: boolean;
  availableSlots: AvailableSlotRaw[];
};

type ListingInfo = {
  id: string;
  title: string;
  description?: string;
  thumbnail?: string;
  price: number;
  currency: string;
  isOnline?: boolean;
  pricingOptions: PricingOption[];
  appointmentScheduleSlots?: any[];
  timezone?: string;
};

type MergedTimeSlot = {
  startMinutes: number;
  endMinutes: number;
  listingIds: string[];
};

type CalendarVisibilityStepProps = {
  listings: ListingInfo[];
  availabilityMap: Map<string, AvailableDate[]>;
  selectedDate: Date | null;
  selectedTime: number | null;
  selectedListingId: string | null;
  onDateChange: (date: Date | null) => void;
  onTimeChange: (time: number | null) => void;
  onListingChange: (listingId: string | null) => void;
  isLoading?: boolean;
  onMonthChange?: (month: Date) => void;
  currentMonth?: Date;
  timezone?: string;
  currency?: string;
  className?: string;
  nextAvailableDateInfo?: { dateISO: string; month: number; year: number } | null;
  isSearchingNextAvailable?: boolean;
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: "£",
  USD: "$",
  EUR: "€",
  AUD: "A$",
  CAD: "C$",
  NZD: "NZ$",
};

function formatPrice(price: number, currency = "GBP"): string {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return `${symbol}${(price / 100).toFixed(2)}`;
}

function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}hr`;
  return `${hours}hr ${mins}min`;
}

function CalendarVisibilityStep({
  listings,
  availabilityMap,
  selectedDate,
  selectedTime,
  selectedListingId,
  onDateChange,
  onTimeChange,
  onListingChange,
  isLoading = false,
  onMonthChange,
  currentMonth,
  timezone = "Europe/London",
  currency = "GBP",
  className,
  nextAvailableDateInfo,
  isSearchingNextAvailable = false,
}: CalendarVisibilityStepProps) {
  const [isDateOpen, setIsDateOpen] = useState(true);
  const [isTimeOpen, setIsTimeOpen] = useState(false);
  const [isListingOpen, setIsListingOpen] = useState(false);

  // Merge all available dates across listings into a unified set
  const mergedAvailableDates = useMemo(() => {
    const dateMap = new Map<string, Set<string>>();

    for (const [listingId, dates] of availabilityMap.entries()) {
      for (const dateEntry of dates) {
        if (dateEntry.isDisabled || !dateEntry.availableSlots?.length) continue;
        const dateStr = moment.tz(dateEntry.dateISO, timezone).format("YYYY-MM-DD");
        if (!dateMap.has(dateStr)) {
          dateMap.set(dateStr, new Set());
        }
        dateMap.get(dateStr)!.add(listingId);
      }
    }

    return dateMap;
  }, [availabilityMap, timezone]);

  // Get merged time slots for selected date
  const mergedTimeSlots = useMemo((): MergedTimeSlot[] => {
    if (!selectedDate) return [];

    const selectedYear = selectedDate.getFullYear();
    const selectedMonth = selectedDate.getMonth();
    const selectedDay = selectedDate.getDate();
    const selectedDateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}`;

    const slotMap = new Map<number, MergedTimeSlot>();

    for (const [listingId, dates] of availabilityMap.entries()) {
      const dateData = dates.find((d) => {
        const dStr = moment.tz(d.dateISO, timezone).format("YYYY-MM-DD");
        return dStr === selectedDateStr;
      });

      if (!dateData || dateData.isDisabled || !dateData.availableSlots) continue;

      for (const slot of dateData.availableSlots) {
        const existing = slotMap.get(slot.startMinutes);
        if (existing) {
          if (!existing.listingIds.includes(listingId)) {
            existing.listingIds.push(listingId);
          }
          if (slot.endMinutes > existing.endMinutes) {
            existing.endMinutes = slot.endMinutes;
          }
        } else {
          slotMap.set(slot.startMinutes, {
            startMinutes: slot.startMinutes,
            endMinutes: slot.endMinutes,
            listingIds: [listingId],
          });
        }
      }
    }

    return Array.from(slotMap.values()).sort(
      (a, b) => a.startMinutes - b.startMinutes,
    );
  }, [selectedDate, availabilityMap, timezone]);

  // Listings available for selected time
  const listingsForSelectedTime = useMemo(() => {
    if (selectedTime === null) return [];
    const slot = mergedTimeSlots.find((s) => s.startMinutes === selectedTime);
    if (!slot) return [];
    return listings.filter((l) => slot.listingIds.includes(l.id));
  }, [selectedTime, mergedTimeSlots, listings]);

  // Auto-select listing if only one is available
  useEffect(() => {
    if (listingsForSelectedTime.length === 1 && !selectedListingId) {
      onListingChange(listingsForSelectedTime[0]!.id);
    }
  }, [listingsForSelectedTime, selectedListingId, onListingChange]);

  const handleDateChange = useCallback(
    (date: Date | undefined) => {
      setIsDateOpen(false);
      setIsTimeOpen(true);
      setIsListingOpen(false);
      onDateChange(date || null);
      onTimeChange(null);
      onListingChange(null);
    },
    [onDateChange, onTimeChange, onListingChange],
  );

  const handleTimeChange = useCallback(
    (time: number) => {
      onTimeChange(time);
      onListingChange(null);
      setIsTimeOpen(false);

      const slot = mergedTimeSlots.find((s) => s.startMinutes === time);
      if (slot && slot.listingIds.length === 1) {
        onListingChange(slot.listingIds[0]!);
        setIsListingOpen(false);
      } else {
        setIsListingOpen(true);
      }
    },
    [mergedTimeSlots, onTimeChange, onListingChange],
  );

  const handleListingChange = useCallback(
    (listingId: string) => {
      onListingChange(listingId);
      setIsListingOpen(false);
    },
    [onListingChange],
  );

  const isDateDisabled = useCallback(
    (date: Date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      return !mergedAvailableDates.has(dateStr);
    },
    [mergedAvailableDates],
  );

  const formattedDate = useMemo(() => {
    if (!selectedDate) return "Choose your preferred date.";
    return selectedDate.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }, [selectedDate]);

  const formattedTime = useMemo(() => {
    if (selectedTime === null) return "Choose your preferred time.";
    const slot = mergedTimeSlots.find((s) => s.startMinutes === selectedTime);
    if (slot) {
      return `${minutesToTime(selectedTime)} - ${minutesToTime(slot.endMinutes)}`;
    }
    return minutesToTime(selectedTime);
  }, [selectedTime, mergedTimeSlots]);

  const selectedListingName = useMemo(() => {
    if (!selectedListingId) return "Choose a service.";
    return listings.find((l) => l.id === selectedListingId)?.title || "Selected";
  }, [selectedListingId, listings]);

  const handleNextAvailable = useCallback(() => {
    if (!nextAvailableDateInfo) return;
    const m = moment.tz(nextAvailableDateInfo.dateISO, timezone);
    const dateObj = new Date(m.year(), m.month(), m.date());
    onMonthChange?.(dateObj);
    onDateChange(dateObj);
    setIsDateOpen(false);
    setIsTimeOpen(true);
  }, [nextAvailableDateInfo, timezone, onMonthChange, onDateChange]);

  const nextAvailableDateFormatted = useMemo(() => {
    if (!nextAvailableDateInfo) return null;
    return moment.tz(nextAvailableDateInfo.dateISO, timezone).format("D MMM YYYY");
  }, [nextAvailableDateInfo, timezone]);

  return (
    <div className={cn("space-y-4", className)}>
      <CheckoutSteps.Header
        title="Select Date & Time"
        description="Choose from available slots across all services."
      />

      {/* Date Selection */}
      <Card>
        <CardHeader
          className="cursor-pointer hover:bg-card-hover/50 transition-colors"
          onClick={() => setIsDateOpen(!isDateOpen)}
        >
          <div className="flex items-center justify-between w-full">
            <div>
              <CardTitle className="text-base">Date</CardTitle>
              <CardDescription
                className={cn(selectedDate && "font-semibold text-foreground")}
              >
                {formattedDate}
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                isDateOpen && "bg-green-100 dark:bg-green-900-muted text-green-500",
              )}
            >
              <CalendarIcon className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>

        {isDateOpen && (
          <CardContent className="pt-0 space-y-4 px-0! sm:px-3!">
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
                <span className="ml-3 text-muted-foreground">
                  Loading available dates...
                </span>
              </div>
            ) : (
              <>
                <div className="w-full flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate || undefined}
                    onSelect={handleDateChange}
                    onMonthChange={onMonthChange}
                    month={currentMonth}
                    disabled={isDateDisabled}
                    captionLayout="dropdown"
                    startMonth={new Date()}
                    endMonth={
                      new Date(
                        new Date().setFullYear(new Date().getFullYear() + 2),
                      )
                    }
                    className="w-full max-w-[320px] sm:max-w-none sm:w-auto rounded-md border p-2 sm:p-3 [--cell-size:36px] sm:[--cell-size:40px]"
                    classNames={{
                      months: "w-full sm:w-auto",
                      month: "w-full sm:w-auto",
                      weekdays: "w-full",
                      weekday: "w-[calc(100%/7)] text-center",
                      week: "w-full",
                      day: "w-[calc(100%/7)]",
                      today: "bg-primary/10 text-primary font-medium rounded-md",
                    }}
                  />
                </div>

                {isSearchingNextAvailable ? (
                  <div className="flex items-center justify-center text-muted-foreground text-sm py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" />
                    Finding available dates...
                  </div>
                ) : nextAvailableDateInfo ? (
                  <div className="flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextAvailable}
                      className="border-primary text-primary hover:bg-primary/20"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      Next Available: {nextAvailableDateFormatted}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </CardContent>
        )}
      </Card>

      {/* Time Selection */}
      {selectedDate && (
        <Card>
          <CardHeader
            className={cn(
              "transition-colors",
              mergedTimeSlots.length > 0 && "cursor-pointer hover:bg-card-hover/50",
            )}
            onClick={() => mergedTimeSlots.length > 0 && setIsTimeOpen(!isTimeOpen)}
          >
            <div className="flex items-center justify-between w-full">
              <div>
                <CardTitle className="text-base">Time</CardTitle>
                <CardDescription
                  className={cn(
                    selectedTime !== null && "font-semibold text-foreground",
                  )}
                >
                  {formattedTime}
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                disabled={mergedTimeSlots.length === 0}
                className={cn(
                  isTimeOpen && "bg-green-100 dark:bg-green-900-muted text-green-500",
                )}
              >
                <Clock className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>

          {isTimeOpen && (
            <CardContent className="pt-0">
              {mergedTimeSlots.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {mergedTimeSlots.map((slot) => (
                    <Button
                      key={slot.startMinutes}
                      variant={
                        slot.startMinutes === selectedTime ? "default" : "outline"
                      }
                      onClick={() => handleTimeChange(slot.startMinutes)}
                      className="w-full"
                    >
                      {minutesToTime(slot.startMinutes)} -{" "}
                      {minutesToTime(slot.endMinutes)}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Clock className="h-12 w-12 text-muted-foreground/70 mb-3" />
                  <p className="text-muted-foreground font-medium">
                    No times available
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Please select a different date
                  </p>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Listing Selection (only when multiple listings match) */}
      {selectedTime !== null && listingsForSelectedTime.length > 1 && (
        <Card>
          <CardHeader
            className="cursor-pointer hover:bg-card-hover/50 transition-colors"
            onClick={() => setIsListingOpen(!isListingOpen)}
          >
            <div className="flex items-center justify-between w-full">
              <div>
                <CardTitle className="text-base">Service</CardTitle>
                <CardDescription
                  className={cn(
                    selectedListingId && "font-semibold text-foreground",
                  )}
                >
                  {selectedListingName}
                </CardDescription>
              </div>
              <Button variant="ghost" size="icon">
                {isListingOpen ? (
                  <ChevronUp className="h-5 w-5" />
                ) : (
                  <ChevronDown className="h-5 w-5" />
                )}
              </Button>
            </div>
          </CardHeader>

          {isListingOpen && (
            <CardContent className="pt-0 space-y-3">
              {listingsForSelectedTime.map((listing) => {
                const isSelected = selectedListingId === listing.id;
                const lowestPrice = listing.pricingOptions.length > 0
                  ? Math.min(...listing.pricingOptions.map((o) => o.price))
                  : listing.price;

                return (
                  <div
                    key={listing.id}
                    onClick={() => handleListingChange(listing.id)}
                    className={cn(
                      "p-4 rounded-lg border-2 cursor-pointer transition-all",
                      isSelected
                        ? "border-primary bg-primary/20"
                        : "border-border hover:border-muted-foreground/50",
                    )}
                  >
                    <div className="flex items-start gap-3">
                      {listing.thumbnail && (
                        <img
                          src={listing.thumbnail}
                          alt={listing.title}
                          className="w-16 h-16 rounded-md object-cover shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-foreground truncate">
                          {listing.title}
                        </h4>
                        {listing.description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {listing.description}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          <p className="font-bold text-primary">
                            {listing.pricingOptions.length > 1 ? "From " : ""}
                            {formatPrice(lowestPrice, listing.currency || currency)}
                          </p>
                          {listing.isOnline && (
                            <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-2 py-0.5 rounded-full">
                              Online
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}

export { CalendarVisibilityStep };
export type {
  CalendarVisibilityStepProps,
  ListingInfo,
  MergedTimeSlot,
};
